import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type { CardOption } from './cardListTypes'
import {
  getSelectedOptionIndices,
  haveSelectedIndicesChanged,
} from './getSelectedOptionIndices'

interface UseCardSelectionProps {
  options: Array<CardOption>
  selection?: Array<string>
  disabled: boolean
  select: 'single' | 'multiple'
  onSelectionChange: (selectedTitles: Array<string>) => void
  onAction?: (action: string) => boolean | void
}

interface UseCardSelectionReturn {
  selectedIndices: Set<number>
  optionClickHandlers: Array<() => void>
}

export function useCardSelection({
  options,
  selection,
  disabled,
  select,
  onSelectionChange,
  onAction,
}: UseCardSelectionProps): UseCardSelectionReturn {
  const optionTitles = useMemo(() => options.map(option => option.title), [options])
  // Use indices for internal selection tracking to handle duplicate titles
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => (
    selection === undefined ? new Set<number>() : getSelectedOptionIndices(selection, optionTitles)
  ))

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange
  const onActionRef = useRef(onAction)
  onActionRef.current = onAction

  const isInternalUpdateRef = useRef(false)

  const validTitles = useMemo(
    () => new Set(optionTitles),
    [optionTitles],
  )

  useEffect(() => {
    if (selection === undefined || isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false
      return
    }

    const nextIndices = getSelectedOptionIndices(selection, optionTitles)
    if (haveSelectedIndicesChanged(selectedIndices, nextIndices)) {
      setSelectedIndices(nextIndices)
    }
  }, [optionTitles, selectedIndices, selection])

  const handleOptionClick = useCallback((index: number) => {
    if (disabled) {
      return
    }

    const clickedOption = options[index]
    if (clickedOption?.action && onActionRef.current?.(clickedOption.action)) {
      return
    }

    isInternalUpdateRef.current = true

    setSelectedIndices(prevSelected => {
      const newSelection = new Set(prevSelected)

      if (select === 'single') {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.clear()
          newSelection.add(index)
        }
      } else {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
      }

      // Convert indices back to titles for the callback
      const selectedTitles = Array.from(newSelection).map(idx => options[idx]?.title).filter(Boolean)

      const otherSelections = (selection ?? []).filter(s => !validTitles.has(s))
      const mergedSelection = [...otherSelections, ...selectedTitles]

      onSelectionChangeRef.current(mergedSelection)
      return newSelection
    })
  }, [disabled, select, selection, validTitles, options])

  const optionClickHandlers = useMemo(() =>
    options.map((_, index) => () => handleOptionClick(index)),
  [options, handleOptionClick])

  return { selectedIndices, optionClickHandlers }
}
